/**
 * MADE-065: OrderDetailDrawer
 * Slide-in drawer showing full detail for any WorkOrder type.
 * Read-only at this stage for roll_shade / assembly / production types.
 * ManufacturingOrder detail is fully rendered.
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Divider,
  IconButton, Alert, Button, CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Inventory as InventoryIcon, LocalShipping as ShipIcon, Download as DownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import { OrderGenerationService } from '../../services/orderGenerationService';
import { CustomerOrderPackSlip } from './CustomerOrderPackSlip';
import { authFetch } from '../../utils/authFetch';
import type { WorkOrder, ManufacturingOrder, ProductionOrder, PackSlipData } from '../../types/madebb.types';

interface OrderDetailDrawerProps {
  workOrder: WorkOrder | null;
  onClose: () => void;
}

const TYPE_LABELS: Record<WorkOrder['type'], string> = {
  manufacturing: '3D Print',
  assembly: 'Assembly',
  production: 'Production',
  roll_shade: 'Roll Shade',
};

const TYPE_COLORS: Record<WorkOrder['type'], string> = {
  manufacturing: '#9c27b0',
  assembly: '#2e7d32',
  production: '#1565c0',
  roll_shade: '#00838f',
};

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-BB', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

export const OrderDetailDrawer: React.FC<OrderDetailDrawerProps> = ({ workOrder, onClose }) => {
  const raw = workOrder?._raw;

  return (
    <Drawer
      anchor="right"
      open={!!workOrder}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
    >
      {workOrder && raw && (
        <Box>
          {/* Drawer header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight={700}>
                {workOrder.orderNumber}
              </Typography>
              <Chip
                label={TYPE_LABELS[workOrder.type]}
                size="small"
                sx={{ bgcolor: TYPE_COLORS[workOrder.type], color: 'white' }}
              />
            </Stack>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Common fields */}
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body2">{workOrder.description}</Typography>
            </Box>

            {workOrder.customerName && (
              <Box>
                <Typography variant="caption" color="text.secondary">Salesperson</Typography>
                <Typography variant="body2">
                  {(raw as ManufacturingOrder).source_order_id ? 'Online Order' : workOrder.customerName}
                </Typography>
              </Box>
            )}
            {(raw as ManufacturingOrder).source_order_id && workOrder.customerName && (
              <Box>
                <Typography variant="caption" color="text.secondary">Customer</Typography>
                <Typography variant="body2">{workOrder.customerName}</Typography>
              </Box>
            )}

            {/* MADE-237: Customer section
                 Field priority: roll-shade fields (customer_first_name etc.) take precedence;
                 shop orders fall back to customer_name / customer_email / pack_slip_data.customer.phone */}
            {(() => {
              const mo = raw as ManufacturingOrder;
              const customerName = (mo.customer_first_name || mo.customer_last_name)
                ? [mo.customer_first_name, mo.customer_last_name].filter(Boolean).join(' ')
                : mo.customer_name || mo.customer_company || null;
              const customerEmail = mo.customer_email_addr || mo.customer_email || null;
              const customerPhone = mo.customer_phone || mo.pack_slip_data?.customer?.phone || null;
              return (
                <>
                  {customerName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                      <Typography variant="body2">{customerName}</Typography>
                    </Box>
                  )}
                  {customerEmail && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2">{customerEmail}</Typography>
                    </Box>
                  )}
                  {customerPhone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2">{customerPhone}</Typography>
                    </Box>
                  )}
                  {(mo.ship_or_collect || mo.shippingMethod) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Fulfilment</Typography>
                      <Typography variant="body2">
                        {/* MADE-247: prefer shippingMethod alias; dhl → 'Ship via DHL', ship → 'Ship', else 'Collect' */}
                        {(() => {
                          const v = mo.shippingMethod ?? mo.ship_or_collect;
                          if (v === 'dhl') return 'Ship via DHL';
                          if (v === 'ship') return 'Ship';
                          return 'Collect';
                        })()}
                      </Typography>
                    </Box>
                  )}
                  {/* Delivery address — shown for ship/dhl orders; collect orders have no stored address */}
                  {(mo.ship_or_collect === 'ship' || mo.ship_or_collect === 'dhl' || mo.shippingMethod === 'dhl') && mo.delivery_address && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Delivery Address</Typography>
                      <Typography variant="body2">
                        {mo.delivery_address.address_line_1}
                        {mo.delivery_address.address_line_2 && <>, {mo.delivery_address.address_line_2}</>}<br />
                        {mo.delivery_address.city}
                        {mo.delivery_address.province && `, ${mo.delivery_address.province}`}<br />
                        {mo.delivery_address.postal_code}<br />
                        {mo.delivery_address.country}
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}

            {workOrder.dueDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Due Date</Typography>
                <Typography
                  variant="body2"
                  color={workOrder.priority === 'urgent' ? 'error' : 'text.primary'}
                  fontWeight={workOrder.priority === 'urgent' ? 700 : 400}
                >
                  {workOrder.priority === 'urgent' && '⚠ '}
                  {formatDate(workOrder.dueDate)}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {workOrder.kanbanStatus.replace(/_/g, ' ')}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* MADE-233: Order reference + sibling readiness for customer orders */}
          {(raw as ManufacturingOrder).disposition_type === 'to_customer' &&
           (raw as ManufacturingOrder).source_order_id && (
            <Box mt={1} mb={2}>
              <Typography variant="caption" color="text.secondary">Order Reference</Typography>
              <Typography variant="body2">
                {(raw as ManufacturingOrder).pack_slip_data?.order_number ??
                 (raw as ManufacturingOrder).source_order_id}
                {((raw as ManufacturingOrder).sibling_count ?? 0) > 0 && (
                  <> · {((raw as ManufacturingOrder).siblings_ready ?? 0) + (['pack_and_ship', 'completed'].includes(workOrder.kanbanStatus ?? '') ? 1 : 0)} of {((raw as ManufacturingOrder).sibling_count ?? 0) + 1} items ready</>
                )}
              </Typography>
            </Box>
          )}

          {/* MADE-094 / MADE-232: Pack & Ship disposition or customer pack slip */}
          {workOrder.kanbanStatus === 'pack_and_ship' && (raw as ManufacturingOrder).pack_slip_data && (
            <>
              {(raw as ManufacturingOrder).pack_slip_data?.shipping !== undefined &&
               (raw as ManufacturingOrder).pack_slip_data?.shipping !== null ? (
                // MADE-232: Customer order — show CustomerOrderPackSlip
                <CustomerOrderPackSlip
                  packSlipData={(raw as ManufacturingOrder).pack_slip_data!}
                  orderNumber={(raw as ManufacturingOrder).pack_slip_data?.order_number}
                />
              ) : (
                // Stock replenishment / legacy — show disposition panel
                <PackSlipDispositionPanel packSlipData={(raw as ManufacturingOrder).pack_slip_data!} />
              )}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* ManufacturingOrder detail */}
          {workOrder.type === 'manufacturing' && (
            <ManufacturingOrderDetail raw={raw as ManufacturingOrder} />
          )}

          {/* ProductionOrder detail */}
          {workOrder.type === 'production' && (
            <ProductionOrderDetail workOrder={workOrder} raw={raw as ProductionOrder} />
          )}

          {/* Roll shade and assembly orders: use ManufacturingOrderDetail */}
          {(workOrder.type === 'roll_shade' || workOrder.type === 'assembly') && (
            <ManufacturingOrderDetail raw={raw as ManufacturingOrder} />
          )}
        </Box>
      )}
    </Drawer>
  );
};

// ── ManufacturingOrder detail panel ──────────────────────────────────────────

const ManufacturingOrderDetail: React.FC<{ raw: ManufacturingOrder }> = ({ raw }) => {
  const handleRegenerateCutSheet = async () => {
    if (!raw.source_order_id) return;
    try {
      const response = await authFetch(`/api/madebb/orders/${raw.source_order_id}`);
      const data = await response.json();
      if (!data.success || !data.order) throw new Error('Order not found');
      const order = data.order;

      const formData = {
        width: order.dimensions?.width ?? order.width,
        drop: order.dimensions?.drop ?? order.drop,
        material: order.material?.name ?? order.material ?? '',
        colour: order.material?.color ?? order.materialColor ?? '',
        hardwareColour: order.hardwareColor ?? '',
      };

      const calculations = {
        fabricYardage: order.fabricYardage,
        pulleyCount: order.pulleyCount,
        pullCordLength: order.pullCordLength,
        fabricCost: order.costs?.fabricCost ?? 0,
        pineCost: order.costs?.pineCost ?? 0,
        bottomPipeCost: order.costs?.bottomPipeCost ?? 0,
        laborCost: order.costs?.laborCost ?? 0,
        totalLaborHours: order.costs?.totalLaborHours ?? 0,
        productionCost: order.costs?.productionCost ?? 0,
        bulkRate: order.costs?.bulkRate ?? 0,
        retailPrice: order.costs?.retailPrice ?? 0,
      };

      await OrderGenerationService.generateOrderForm(
        formData as any,
        calculations as any,
        order.orderNumber,
        order.securityToken
      );
    } catch (err) {
      console.error('Failed to regenerate cut sheet:', err);
      alert('Could not regenerate cut sheet. Please try again.');
    }
  };

  return (
  <Stack spacing={1.5}>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} mb={1}>Manufacturing Detail</Typography>
    </Box>

    {/* MADE-240: Regenerate Cut Sheet */}
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={600}>Cut Sheet</Typography>
      {raw.source_order_id ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handleRegenerateCutSheet}
          sx={{ mt: 0.5 }}
        >
          Regenerate Cut Sheet
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No source order linked
        </Typography>
      )}
    </Box>

    <Box>
      <Typography variant="caption" color="text.secondary">Material</Typography>
      <Typography variant="body2">{raw.material_name}</Typography>
    </Box>

    {raw.dimensions && (
      <Box>
        <Typography variant="caption" color="text.secondary">Dimensions</Typography>
        <Typography variant="body2">{raw.dimensions}</Typography>
      </Box>
    )}

    <Box>
      <Typography variant="caption" color="text.secondary">Quantity</Typography>
      <Typography variant="body2">{raw.quantity} {raw.unit}</Typography>
    </Box>

    {raw.assigned_machine && (
      <Box>
        <Typography variant="caption" color="text.secondary">Assigned Machine</Typography>
        <Typography variant="body2">{raw.assigned_machine}</Typography>
      </Box>
    )}

    {raw.bom && raw.bom.length > 0 && (
      <Box>
        <Typography variant="caption" color="text.secondary">Bill of Materials</Typography>
        {raw.bom.map((b, i) => (
          <Typography key={i} variant="body2">• {b.quantity} {b.unit || ''} {b.materialId}</Typography>
        ))}
      </Box>
    )}

    {raw.missing_specs && raw.missing_specs.length > 0 && (
      <Alert severity="warning" icon={false}>
        <Typography variant="caption">Missing specs: {raw.missing_specs.join(', ')}</Typography>
      </Alert>
    )}

    {raw.qc_rejection_reason && (
      <Alert severity="error" icon={false}>
        <Typography variant="caption">QC Rejection: {raw.qc_rejection_reason}</Typography>
      </Alert>
    )}

    <Box>
      <Typography variant="caption" color="text.secondary">Created</Typography>
      <Typography variant="body2">{new Date(raw.created_at).toLocaleDateString('en-BB')}</Typography>
    </Box>
  </Stack>
  );
};

// ── MADE-094: Pack & Ship — Split Disposition Panel ───────────────────────────
// Shown in the right-hand detail pop-out when a pack_and_ship order card has
// pack_slip_data with split disposition instructions.

const ACTION_LABELS: Record<string, string> = {
  place_in_inventory:   'Place in inventory',
  place_with_order:     'Place with order',
  pick_from_inventory:  'Pick from inventory',
  pick_from_production: 'Pick from production',
  return_to_inventory:  'Return to inventory',
};

const PackSlipDispositionPanel: React.FC<{ packSlipData: PackSlipData }> = ({ packSlipData }) => {
  const allInstructions = packSlipData.items?.flatMap(item => item.disposition_instructions ?? []) ?? [];

  // Simple direct-to-ship case (no split)
  const isDirectToShip =
    packSlipData.direct_to_ship === true ||
    (packSlipData.items?.length === 1 && packSlipData.items[0].direct_to_ship === true && packSlipData.items[0].inventory_qty === 0);

  if (isDirectToShip) {
    return (
      <Box data-testid="pack-slip-disposition">
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Disposition Instructions
        </Typography>
        <Alert severity="info" icon={<ShipIcon fontSize="small" />} sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>Direct to ship</Typography>
          <Typography variant="caption" color="text.secondary">
            No inventory step required — place produced units directly with this order.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (allInstructions.length === 0) return null;

  return (
    <Box data-testid="pack-slip-disposition">
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        Disposition Instructions
      </Typography>
      <Stack spacing={0.75}>
        {allInstructions.map((instr, idx) => {
          const isInventory = instr.action === 'place_in_inventory' || instr.action === 'return_to_inventory';
          const isOrder = instr.action === 'place_with_order' || instr.action === 'pick_from_production';
          return (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: isInventory ? '#e8f5e9' : isOrder ? '#e3f2fd' : 'grey.100',
                border: `1px solid ${isInventory ? '#a5d6a7' : isOrder ? '#90caf9' : '#e0e0e0'}`,
              }}
            >
              {isInventory
                ? <InventoryIcon sx={{ fontSize: 16, color: '#388e3c' }} />
                : <ShipIcon sx={{ fontSize: 16, color: '#1976d2' }} />
              }
              <Box>
                <Typography variant="caption" fontWeight={700} color={isInventory ? '#2e7d32' : '#1565c0'}>
                  {ACTION_LABELS[instr.action] ?? instr.action.replace(/_/g, ' ')}: {instr.qty} unit{instr.qty !== 1 ? 's' : ''}
                </Typography>
                {instr.order_number && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Order: {instr.order_number}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

// ── ProductionOrder detail panel ──────────────────────────────────────────────

const ProductionOrderDetail: React.FC<{ workOrder: WorkOrder; raw: ProductionOrder }> = ({ workOrder }) => (
  <Stack spacing={1.5}>
    <Typography variant="subtitle2" fontWeight={700}>Production Order Detail</Typography>

    {workOrder.blockedOn && (
      <Alert severity="error">
        <Typography variant="body2" fontWeight={700}>
          Blocked on {workOrder.blockedOn === 'procurement' ? 'Procurement' : 'Production'}
        </Typography>
      </Alert>
    )}

    {workOrder.blockedChildJobs && workOrder.blockedChildJobs.length > 0 && (
      <Box>
        <Typography variant="caption" color="text.secondary">Outstanding Child Jobs</Typography>
        {workOrder.blockedChildJobs.map(id => (
          <Typography key={id} variant="body2">• {id}</Typography>
        ))}
      </Box>
    )}

    {workOrder.procurementBlockers && workOrder.procurementBlockers.length > 0 && (
      <Box>
        <Typography variant="caption" color="text.secondary">Procurement Blockers</Typography>
        {workOrder.procurementBlockers.map(b => (
          <Typography key={b.materialId} variant="body2">
            • {b.materialName} × {b.quantity}
          </Typography>
        ))}
      </Box>
    )}

    <SubmittedFilesSection orderId={(raw as ManufacturingOrder).id} />
  </Stack>
);

/* ── Submitted Files Section ────────────────────────────────────────── */
interface SubmittedFile {
  filename: string;
  size_bytes: number;
  uploaded_at: string;
  bucket_key: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-BB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function SubmittedFilesSection({ orderId }: { orderId: string }) {
  const [files, setFiles] = useState<SubmittedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    authFetch(`/api/madebb/manufacturing-orders/${orderId}/submitted-files`)
      .then(r => r.json())
      .then(d => setFiles(d.files ?? []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleDownload = async (bucketKey: string) => {
    setDownloadingKey(bucketKey);
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${orderId}/submitted-files/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket_key: bucketKey }),
      });
      const { download_url } = await res.json();
      if (download_url) window.open(download_url, '_blank', 'noopener');
    } catch { /* ignore */ } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Submitted Files</Typography>
      {loading ? (
        <CircularProgress size={18} />
      ) : files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No files attached to this inquiry.</Typography>
      ) : (
        <Stack spacing={1}>
          {files.map(f => (
            <Box key={f.bucket_key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap fontWeight={500}>{f.filename}</Typography>
                <Typography variant="caption" color="text.secondary">{formatBytes(f.size_bytes)} &middot; {formatUploadDate(f.uploaded_at)}</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloadingKey === f.bucket_key ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                disabled={downloadingKey === f.bucket_key}
                onClick={() => handleDownload(f.bucket_key)}
              >
                Download
              </Button>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
